import { forwardRef, type ButtonHTMLAttributes, type CSSProperties, type ReactNode } from 'react'

export interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Pressed. A chip is a filter, so this is `aria-pressed`, not a colour choice. */
  on?: boolean
  /** A colour, and it must be a token reference — `var(--d-film)`, not
   *  `#2F4A7A`. Rule 1 says no component reads a colour that is not a token;
   *  a discipline hue is chosen per row at runtime, so the value arrives
   *  through a custom property rather than a class, and the caller owes the
   *  token. */
  dot?: string
  /** How many things the filter would leave. Absent and 0 both draw nothing:
   *  a filter that would return nothing says so by being empty, not by
   *  wearing a zero. */
  count?: number
  children: ReactNode
}

/** A filter you tap. Pill, 36px drawn inside a 44px tap target. */
export const Chip = forwardRef<HTMLButtonElement, ChipProps>(function Chip(
  { on = false, dot, count, className = '', children, style, ...rest },
  ref,
) {
  const classes = ['u-chip', on ? 'is-on' : '', className].filter(Boolean).join(' ')
  return (
    <button
      ref={ref}
      type="button"
      className={classes}
      aria-pressed={on}
      style={dot ? ({ ...style, '--u-dot': dot } as CSSProperties) : style}
      {...rest}
    >
      <span className="u-chip__face">
        {dot && <span className="u-chip__dot" aria-hidden="true" />}
        {children}
        {!!count && <span className="u-chip__count">{count}</span>}
      </span>
    </button>
  )
})
